import { Node } from '@tiptap/core'
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react'
import React, { useRef, useCallback, useEffect } from 'react'

function ButtonBlockView({ node, updateAttributes, selected, editor, getPos }) {
    const containerRef = useRef(null)
    const startX = useRef(0)
    const startWidth = useRef(0)
    const side = useRef(null)
    const moveListenerRef = useRef(null)
    const upListenerRef = useRef(null)

    const width = node.attrs.width || 200
    const align = node.attrs.align || 'center'

    // Clean up resize listeners on unmount to prevent leaks
    useEffect(() => {
        return () => {
            if (moveListenerRef.current) document.removeEventListener('mousemove', moveListenerRef.current)
            if (upListenerRef.current) document.removeEventListener('mouseup', upListenerRef.current)
        }
    }, [])

    const selectThisNode = useCallback(() => {
        if (editor && getPos) {
            const pos = getPos()
            if (typeof pos === 'number') {
                editor.commands.setNodeSelection(pos)
            }
        }
    }, [editor, getPos])

    const onMouseDown = useCallback((e, resizeSide) => {
        e.preventDefault()
        e.stopPropagation()
        side.current = resizeSide
        startX.current = e.clientX
        startWidth.current = width

        const onMouseMove = (moveEvent) => {
            const diff = moveEvent.clientX - startX.current
            const delta = side.current === 'right' ? diff * 2 : -diff * 2
            const newWidth = Math.max(120, Math.min(560, startWidth.current + delta))
            updateAttributes({ width: Math.round(newWidth) })
        }

        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove)
            document.removeEventListener('mouseup', onMouseUp)
            moveListenerRef.current = null
            upListenerRef.current = null
        }

        moveListenerRef.current = onMouseMove
        upListenerRef.current = onMouseUp
        document.addEventListener('mousemove', onMouseMove)
        document.addEventListener('mouseup', onMouseUp)
    }, [width, updateAttributes])

    return (
        <NodeViewWrapper contentEditable={false} data-drag-handle>
            <div style={{ textAlign: align, padding: '0' }} onClick={selectThisNode}>
                <div
                    ref={containerRef}
                    style={{
                        position: 'relative',
                        display: 'inline-block',
                        width: `${width}px`,
                        maxWidth: '100%',
                    }}
                >
                    {/* The button */}
                    <span
                        role="button"
                        onClick={(e) => e.preventDefault()}
                        style={{
                            display: 'block',
                            backgroundColor: '#115E59',
                            color: '#ffffff',
                            padding: '10px 16px',
                            borderRadius: '6px',
                            fontWeight: '600',
                            fontSize: '14px',
                            letterSpacing: '0.01em',
                            textAlign: 'center',
                            textDecoration: 'none',
                            outline: selected ? '2px solid #5eead4' : 'none',
                            outlineOffset: '3px',
                            cursor: 'grab',
                            userSelect: 'none',
                        }}
                    >
                        {node.attrs.text || 'Click Here'}
                    </span>

                    {/* Resize handles */}
                    {selected && (
                        <>
                            <div
                                onMouseDown={(e) => onMouseDown(e, 'left')}
                                style={{
                                    position: 'absolute',
                                    left: '-4px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: '8px',
                                    height: '24px',
                                    backgroundColor: '#115E59',
                                    borderRadius: '3px',
                                    cursor: 'ew-resize',
                                    opacity: 0.7,
                                }}
                            />
                            <div
                                onMouseDown={(e) => onMouseDown(e, 'right')}
                                style={{
                                    position: 'absolute',
                                    right: '-4px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: '8px',
                                    height: '24px',
                                    backgroundColor: '#115E59',
                                    borderRadius: '3px',
                                    cursor: 'ew-resize',
                                    opacity: 0.7,
                                }}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* Controls when selected */}
            {selected && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', paddingBottom: '8px', alignItems: 'center' }}>
                    {/* Alignment */}
                    <div style={{ display: 'flex', gap: '2px', background: '#f3f4f6', borderRadius: '6px', padding: '2px' }}>
                        {[
                            { key: 'left', icon: (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="18" y2="18"/></svg>
                            )},
                            { key: 'center', icon: (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="6" y1="12" x2="18" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></svg>
                            )},
                            { key: 'right', icon: (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="6" y1="18" x2="21" y2="18"/></svg>
                            )},
                        ].map(({ key, icon }) => (
                            <button
                                type="button"
                                key={key}
                                onClick={() => updateAttributes({ align: key })}
                                onMouseDown={(e) => e.stopPropagation()}
                                style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    border: 'none',
                                    backgroundColor: align === key ? '#fff' : 'transparent',
                                    boxShadow: align === key ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                                    color: align === key ? '#115E59' : '#9ca3af',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                {icon}
                            </button>
                        ))}
                    </div>

                    {/* Label & URL */}
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#6b7280' }}>
                        <span>Label:</span>
                        <input
                            type="text"
                            value={node.attrs.text}
                            onChange={(e) => updateAttributes({ text: e.target.value })}
                            onMouseDown={(e) => e.stopPropagation()}
                            style={{
                                fontSize: '12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                padding: '3px 8px',
                                width: '120px',
                                outline: 'none',
                                fontFamily: 'inherit',
                            }}
                        />
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#6b7280' }}>
                        <span>URL:</span>
                        <input
                            type="text"
                            value={node.attrs.url}
                            onChange={(e) => updateAttributes({ url: e.target.value })}
                            onMouseDown={(e) => e.stopPropagation()}
                            placeholder="https://… or {{onboardingUrl}}"
                            style={{
                                fontSize: '12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                padding: '3px 8px',
                                width: '220px',
                                outline: 'none',
                                fontFamily: 'ui-monospace, monospace',
                            }}
                        />
                    </label>
                    </div>
                </div>
            )}
        </NodeViewWrapper>
    )
}

export const ButtonBlockNode = Node.create({
    name: 'buttonBlock',
    group: 'block',
    atom: true,
    selectable: true,
    draggable: true,

    addAttributes() {
        return {
            text: { default: 'Click Here' },
            url: { default: '' },
            width: { default: 200 },
            align: { default: 'center' },
        }
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="buttonBlock"]',
                getAttrs: (el) => {
                    const a = el.querySelector('a')
                    return {
                        text: a?.textContent?.trim() || 'Click Here',
                        url: a?.getAttribute('href') || '',
                        width: parseInt(el.getAttribute('data-width')) || 200,
                        align: el.getAttribute('data-align') || 'center',
                    }
                },
            },
        ]
    },

    renderHTML({ node }) {
        const url = node.attrs.url || '#'
        const text = node.attrs.text || 'Click Here'
        const width = node.attrs.width || 200
        const align = node.attrs.align || 'center'

        return [
            'div',
            {
                'data-type': 'buttonBlock',
                'data-width': String(width),
                'data-align': align,
                style: `text-align: ${align}; padding: 0;`,
            },
            [
                'a',
                {
                    href: url,
                    style: `display: inline-block; width: ${width}px; max-width: 100%; background-color: #115E59; color: #ffffff; padding: 10px 16px; border-radius: 6px; text-decoration: none; font-weight: 600; font-size: 14px; letter-spacing: 0.01em; text-align: center; box-sizing: border-box;`,
                },
                text,
            ],
        ]
    },

    addNodeView() {
        return ReactNodeViewRenderer(ButtonBlockView)
    },

    addCommands() {
        return {
            insertButtonBlock:
                () =>
                ({ commands }) =>
                    commands.insertContent({
                        type: this.name,
                        attrs: { text: 'Click Here', url: '' },
                    }),
        }
    },
})
